// NPM MODULES
import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';

// OUR COMPONENTS
import TopCustomerTable from '../../components/reports/topcustomers/TopCustomerTable';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import Layout from '../../components/layouts/Layout';
import DataExportModal from '../../components/common/modals/DataExportModal';
import TopCustomerTableRow from '../../components/reports/topcustomers/TopCustomerTableRow';
import Loader from '../../components/common/Loader';

// NETWORKING
import useTopCustomerReportRequest from '../../requests/TopCustomerReportRequest';

// FORMATTED MESSAGES
import TopCustomerReportsFormattedMessages from '../../messages/reports/TopCustomerReportsFormattedMessages';

// ROUTES
import { ReportsRoutes } from '../../routes/ReportsRoutes';

const TopUser = ({
  customer,
  topUserData,
  updateActingProviderDetails,
  updateActingCustomerDetails,
  navigate,
  errorHandler,
  accessLevel,
  accessType,
}) => {
  const [smsList, setSmsList] = useState([]);
  const [showList, setShowList] = useState(false);
  const [data, setData] = useState([]);
  const [dataExists, setDataExists] = useState(false);
  const [totals, setTotals] = useState({
    activeUsers: 0,
    standardSent: 0,
    standardParts: 0,
    directSent: 0,
    directParts: 0,
    repliesSent: 0,
    repliesParts: 0,
    sentSent: 0,
    sentParts: 0,
  });
  const [selectedData, setSelectedData] = useState(null);
  const [activePage, setActivePage] = useState('list');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const { getReportData, exportTopUserReport } = useTopCustomerReportRequest();

  useEffect(() => {
    if (customer && customer !== null) {
      if (topUserData && topUserData.length !== 0) {
        setData(topUserData);
        setDataExists(true);
        formatData(topUserData);
      }
    }
  }, [customer, topUserData]);

  /*
    Convenience function to handle page navigation.
    TODO - Use React History to handle this
  */
  const handleNavigation = (target) => {
    window.location.href = '/reports/' + target;
  };

  /*
    Formats the data in a manner required for the Table
    to properly display the data
  */
  const formatData = (data) => {
    try {
      if (data.length === 0) {
        setSmsList([]);
        setDataExists(true);
        return;
      }

      const formattedData = [];
      const newTotals = {
        activeUsers: 0,
        standardSent: 0,
        standardParts: 0,
        directSent: 0,
        directParts: 0,
        repliesSent: 0,
        repliesParts: 0,
        sentSent: 0,
        sentParts: 0,
      };

      data.forEach((el) => {
        formattedData.push(
          <TopCustomerTableRow
            key={'list' + el.customer_account_no}
            id={el.customer_account_no}
            name={el.customer_name}
            provider={el.provider_name}
            activeUsers={el.userCount}
            Direct={{
              sent: el.direct_sent,
              parts: el.direct_parts,
            }}
            Standard={{
              sent: el.messages_sent,
              parts: el.messages_parts,
            }}
            Replies={{
              sent: el.replies_sent,
              parts: el.replies_parts,
            }}
            Sent={{
              sent: el.reminders_sent,
              parts: el.reminders_parts,
            }}
            onClickName={() => changePage('svCustomer', el)}
          />
        );

        newTotals.activeUsers += parseInt(el.userCount, 10);
        newTotals.standardSent += parseInt(el.messages_sent, 10);
        newTotals.standardParts += parseInt(el.messages_parts, 10);
        newTotals.directSent += parseInt(el.direct_sent, 10);
        newTotals.directParts += parseInt(el.direct_parts, 10);
        newTotals.repliesSent += parseInt(el.replies_sent, 10);
        newTotals.repliesParts += parseInt(el.replies_parts, 10);
        newTotals.sentSent += parseInt(el.reminders_sent, 10);
        newTotals.sentParts += parseInt(el.reminders_parts, 10);
      });

      setSmsList(formattedData);
      setDataExists(true);
      setShowList(true);
      setTotals(newTotals);
    } catch (e) {
      console.log('data error:', e);
    }
  };

  const changePage = (page, value) => {
    if (page === 'svCustomer') {
      const customerId = value.customer_id;
      const providerId = value.provider_id;
      window.history.pushState(
        customerId,
        `Customer:${customerId}`,
        `/admin/customers/${customerId}`
      );
      updateActingProviderDetails(providerId);
      updateActingCustomerDetails(customerId);
      navigate('/admin/customers/' + customerId);
    }
  };

  const backButton = () => {
    setActivePage('list');
  };

  /*
    Handles searching User Data between two dates
  */
  const handleTopUserDataSearch = async (fromDate, toDate, timeRange) => {
    //loader
    setDataExists(false);
    setShowList(false);

    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      timeRange: timeRange,
      timeRangeStart: fromDate,
      timeRangeEnd: toDate,
    };
    const result = await getReportData(searchParams, errorHandler);

    setData(result);
    setFromDate(fromDate);
    setToDate(toDate);
    formatData(result);
  };

  useEffect(() => {
    if (fromDate && toDate) handleTopUserDataSearch(fromDate, toDate);
  }, [fromDate, toDate]);

  const handleExportTopUserReport = async () => {
    setExporting(true);
    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
    };
    const result = await exportTopUserReport(searchParams, errorHandler);
    console.log('export result: ', result);
    setShowEmailSentModal(true);
    setEmailRecipient(result);
    setExporting(false);
  };

  /*
    Used when clicking on a Top User Customer Id or Customer's Name.  Takes
    you back to the Cost Centre Standard Report
  */
  const showCostCentreReport = (value) => {
    console.log('customer id: ', value);
    handleNavigation('centers');
  };

  /*
    Generates the Search Tools for querying the data
  */
  const generateSearchTools = () => {
    return (
      <Box sx={{ marginBottom: { xs: 4, md: 2 } }}>
        <DateRangeSelector
          handleDateSearch={handleTopUserDataSearch}
          parentStartDate={fromDate}
          setParentStartDate={setFromDate}
          parentEndDate={toDate}
          setParentEndDate={setToDate}
        />
      </Box>
    );
  };

  const reloadPage = () => {
    window.location.reload(1);
  };

  const generateExportButton = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 2,
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={handleExportTopUserReport}
          disabled={exporting}
        >
          Email Report
        </Button>
      </Box>
    );
  };

  if (accessLevel === '3' && accessType === '3') {
    return (
      <Layout
        title={TopCustomerReportsFormattedMessages.topUserReportsTitle}
        routes={ReportsRoutes}
        accessLevel={accessLevel}
        accessType={accessType}
      >
        {generateSearchTools()}
        {generateExportButton()}
        {!dataExists ? (
          <Loader />
        ) : (
          <TopCustomerTable totals={totals} list={smsList} />
        )}
        <DataExportModal
          show={showEmailSentModal}
          onHide={() => setShowEmailSentModal(false)}
          emailRecipient={emailRecipient}
        />
      </Layout>
    );
  }
  return null;
};

export default TopUser;
